/**
 * Owner: Masa Bayu
 * File: qx-writer-document-store.ts
 * Date:Time: 2026-10-24 18:20
 * Description: C1-2 — SoT dokumen QXW writer di luar komponen; penala (setter)
 *   serasi `Dispatch<SetStateAction<T>>` supaya pemanggil lama tak berubah tandatangan.
 */

import { useSyncExternalStore } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { QXWDocument } from '@qx/qxw-core';
import { withDocTitle } from './qx-doc-title';
import { createBlankDocument } from './qx-writer-sync';

export type QxWriterDocumentSnapshot = {
  readonly doc: QXWDocument;
  readonly fileHandle: FileSystemFileHandle | null;
  readonly isDirty: boolean;
  readonly importPdfBytes: Uint8Array | null;
};

let snapshot: QxWriterDocumentSnapshot = {
  doc: createBlankDocument(),
  fileHandle: null,
  isDirty: false,
  importPdfBytes: null,
};

const listeners = new Set<() => void>();

function apply(next: QxWriterDocumentSnapshot): void {
  if (next === snapshot) return;
  snapshot = next;
  for (const listener of listeners) listener();
}

export function getQxWriterDocumentSnapshot(): QxWriterDocumentSnapshot {
  return snapshot;
}

export function subscribeQxWriterDocument(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Bacaan reaktif shell; tulis melalui `setWriter*` di bawah. */
export function useQxWriterDocument(): QxWriterDocumentSnapshot {
  return useSyncExternalStore(subscribeQxWriterDocument, getQxWriterDocumentSnapshot);
}

export const setWriterDocument: Dispatch<SetStateAction<QXWDocument>> = (next) => {
  apply({ ...snapshot, doc: typeof next === 'function' ? next(snapshot.doc) : next });
};

export const setWriterFileHandle: Dispatch<SetStateAction<FileSystemFileHandle | null>> = (next) => {
  apply({
    ...snapshot,
    fileHandle: typeof next === 'function' ? next(snapshot.fileHandle) : next,
  });
};

export const setWriterDirty: Dispatch<SetStateAction<boolean>> = (next) => {
  apply({ ...snapshot, isDirty: typeof next === 'function' ? next(snapshot.isDirty) : next });
};

export function setWriterImportPdfBytes(bytes: Uint8Array | null): void {
  apply({ ...snapshot, importPdfBytes: bytes });
}

/** Studio buka kosong — buang naskhah sesi pelayar dikongsi (bukan akaun). */
export function resetWriterDocumentToBlank(): void {
  apply({
    doc: createBlankDocument(),
    fileHandle: null,
    isDirty: false,
    importPdfBytes: null,
  });
}

/** §473 U1-H5 — tulis tajuk dokumen melalui SoT (bar Studio + WriterTopNav). */
export function applyDocTitle(title: string): void {
  setWriterDocument((prev) => withDocTitle(prev, title));
  setWriterDirty(true);
}
